import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileSpreadsheet, Download, FileText, Users, DollarSign, Calendar, Target, Receipt } from 'lucide-react';

import API_URL from '../utils/api';

const REPORTS = [
    {
        id: 'payroll', title: 'Payroll Report', description: 'Employee salary details with earnings and deductions',
        icon: DollarSign, color: 'bg-emerald-50 text-emerald-600',
        filters: [
            { key: 'month', label: 'Month', type: 'select', options: Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i] })) },
            { key: 'year', label: 'Year', type: 'select', options: Array.from({ length: 5 }, (_, i) => { const y = new Date().getFullYear() - i; return { value: String(y), label: String(y) }; }) },
        ]
    },
    {
        id: 'attendance', title: 'Attendance Report', description: 'Employee check-in/out records with hours and locations',
        icon: Calendar, color: 'bg-blue-50 text-[#2563eb]',
        filters: [
            { key: 'start_date', label: 'Start Date', type: 'date' },
            { key: 'end_date', label: 'End Date', type: 'date' },
        ]
    },
    {
        id: 'expenses', title: 'Expenses Report', description: 'Expense claims with amounts, categories, and approval status',
        icon: Receipt, color: 'bg-amber-100 text-amber-600',
        filters: [
            { key: 'status', label: 'Status', type: 'select', options: [{ value: 'all', label: 'All Status' }, { value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }] },
        ]
    },
    {
        id: 'performance', title: 'Performance Report', description: 'Goals, KPIs, and performance review ratings',
        icon: Target, color: 'bg-purple-100 text-purple-600',
        filters: []
    },
    {
        id: 'leaves', title: 'Leaves Report', description: 'Leave requests with types, dates, and approval status',
        icon: Calendar, color: 'bg-teal-100 text-teal-600',
        filters: []
    },
    {
        id: 'employees', title: 'Employees Report', description: 'Employee directory with departments, roles, and status',
        icon: Users, color: 'bg-indigo-100 text-indigo-600',
        filters: []
    },
];

const Reports = () => {
    const [downloading, setDownloading] = useState(null);
    const [filters, setFilters] = useState({});

    const handleDownload = async (reportId, format) => {
        setDownloading(`${reportId}-${format}`);
        try {
            const params = new URLSearchParams();
            params.append('format', format);
            const reportFilters = filters[reportId] || {};
            Object.entries(reportFilters).forEach(([key, value]) => {
                if (value && value !== 'all') params.append(key, value);
            });

            const response = await axios.get(`${API_URL}/api/reports/${reportId}?${params.toString()}`, {
                withCredentials: true,
                responseType: 'blob'
            });

            const ext = format === 'csv' ? 'csv' : 'xlsx';
            const mimeType = format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${reportId}_report.${ext}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert('Error downloading report');
        } finally {
            setDownloading(null);
        }
    };

    const updateFilter = (reportId, key, value) => {
        setFilters(prev => ({
            ...prev,
            [reportId]: { ...(prev[reportId] || {}), [key]: value }
        }));
    };

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Reports & Export</h1>
                <p className="text-slate-500 mt-1">Generate and download reports in Excel or CSV format</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {REPORTS.map(report => (
                    <Card key={report.id} className="rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden bg-white" data-testid={`report-card-${report.id}`}>
                        <CardContent className="p-6">
                            <div className="flex items-start gap-4 mb-4">
                                <div className={`p-3 rounded-lg ${report.color.split(' ')[0]}`}>
                                    <report.icon className={`h-6 w-6 ${report.color.split(' ')[1]}`} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{report.title}</h3>
                                    <p className="text-sm text-slate-500 mt-0.5">{report.description}</p>
                                </div>
                            </div>

                            {report.filters.length > 0 && (
                                <div className="space-y-3 mb-4 pb-4 border-b border-slate-100">
                                    {report.filters.map(filter => (
                                        <div key={filter.key}>
                                            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{filter.label}</Label>
                                            {filter.type === 'date' ? (
                                                <Input type="date" className="rounded-lg mt-1 h-9 text-sm bg-slate-50 border-slate-200" value={(filters[report.id] || {})[filter.key] || ''} onChange={(e) => updateFilter(report.id, filter.key, e.target.value)} data-testid={`filter-${report.id}-${filter.key}`} />
                                            ) : (
                                                <Select value={(filters[report.id] || {})[filter.key] || (filter.options[0]?.value)} onValueChange={(v) => updateFilter(report.id, filter.key, v)}>
                                                    <SelectTrigger className="rounded-lg mt-1 h-9 text-sm bg-slate-50 border-slate-200" data-testid={`filter-${report.id}-${filter.key}`}><SelectValue /></SelectTrigger>
                                                    <SelectContent className="rounded-lg">
                                                        {filter.options.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="flex gap-2">
                                <Button
                                    className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm h-9 text-sm"
                                    onClick={() => handleDownload(report.id, 'excel')}
                                    disabled={downloading === `${report.id}-excel`}
                                    data-testid={`download-${report.id}-excel`}
                                >
                                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                                    {downloading === `${report.id}-excel` ? 'Downloading...' : 'Excel'}
                                </Button>
                                <Button
                                    variant="outline"
                                    className="flex-1 rounded-lg h-9 text-sm"
                                    onClick={() => handleDownload(report.id, 'csv')}
                                    disabled={downloading === `${report.id}-csv`}
                                    data-testid={`download-${report.id}-csv`}
                                >
                                    <FileText className="h-4 w-4 mr-2" />
                                    {downloading === `${report.id}-csv` ? 'Downloading...' : 'CSV'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default Reports;
